import { useCallback, useEffect, useRef, useState } from "react";
import type { Client, Room } from "@colyseus/sdk";
import type { PortalSnapshot, SocialRequest, SocialRoomCard } from "../../shared/portal";
import { api, type User } from "../api";

export type PortalConnectionState = "connecting" | "online" | "reconnecting" | "offline";

const EMPTY_SNAPSHOT: PortalSnapshot = { online: [], messages: [] };
const TOKEN_KEY = "omrPortalReconnectionToken";

export interface PortalRealtime {
  snapshot: PortalSnapshot;
  connectionState: PortalConnectionState;
  error: string;
  sendMessage(text: string): void;
  deleteMessage(messageId: string): void;
  clearError(): void;
  request(value:Omit<SocialRequest,"id">):Promise<{room?:SocialRoomCard}>;
}

export function usePortalRealtime(
  client: Client,
  user: User | null,
  onSessionExpired: () => void,
): PortalRealtime {
  const [snapshot, setSnapshot] = useState<PortalSnapshot>(EMPTY_SNAPSHOT);
  const [connectionState, setConnectionState] = useState<PortalConnectionState>("offline");
  const [error, setError] = useState("");
  const roomRef = useRef<Room | null>(null);
  const pending=useRef(new Map<string,{resolve:(value:{room?:SocialRoomCard})=>void;reject:(error:Error)=>void;timer:number}>());

  useEffect(() => {
    if (!user) {
      setSnapshot(EMPTY_SNAPSHOT);
      setConnectionState("offline");
      return;
    }
    let stopped = false;
    let heartbeat: number | undefined;
    let retry: number | undefined;

    const scheduleConnect = () => {
      if (stopped || retry !== undefined) return;
      retry = window.setTimeout(() => {
        retry = undefined;
        void connect();
      }, 1_500);
    };

    const attach = (room: Room) => {
      if (stopped) {
        void room.leave().catch(() => undefined);
        return;
      }
      roomRef.current = room;
      sessionStorage.setItem(TOKEN_KEY, room.reconnectionToken);
      setConnectionState("online");
      setError("");
      room.onMessage("portal:snapshot", (next: PortalSnapshot) => setSnapshot(next));
      room.onMessage("social:response",(value:{id:string;ok:boolean;error?:string;room?:SocialRoomCard})=>{
        const task=pending.current.get(value.id);if(!task)return;
        clearTimeout(task.timer);pending.current.delete(value.id);
        if(value.ok)task.resolve({room:value.room});else task.reject(Error(value.error??"Ошибка общения"));
      });
      room.onMessage("portal:error", (payload: { error?: string }) => {
        const message = payload?.error || "Ошибка realtime-соединения";
        setError(message);
        if (message.includes("Сессия завершена")) onSessionExpired();
      });
      room.onDrop(() => setConnectionState("reconnecting"));
      room.onReconnect(() => {
        sessionStorage.setItem(TOKEN_KEY, room.reconnectionToken);
        setConnectionState("online");
        room.send("portal:sync");
      });
      room.onLeave(() => {
        if (roomRef.current === room) roomRef.current = null;
        if (heartbeat !== undefined) window.clearInterval(heartbeat);
        sessionStorage.removeItem(TOKEN_KEY);
        if (!stopped) {
          setConnectionState("offline");
          scheduleConnect();
        }
      });
      room.send("portal:sync");
      heartbeat = window.setInterval(() => room.send("portal:heartbeat"), 15_000);
    };

    const connect = async () => {
      if (stopped) return;
      setConnectionState("connecting");
      const reconnectionToken = sessionStorage.getItem(TOKEN_KEY);
      if (reconnectionToken) {
        try {
          attach(await client.reconnect(reconnectionToken));
          return;
        } catch {
          sessionStorage.removeItem(TOKEN_KEY);
        }
      }
      try {
        const { ticket } = await api<{ ticket: string }>("/api/realtime-ticket", { method: "POST" });
        attach(await client.joinOrCreate("portal", { ticket }));
      } catch (connectionError) {
        if (stopped) return;
        const message = connectionError instanceof Error ? connectionError.message : "Нет связи с порталом";
        setError(message);
        setConnectionState("offline");
        if (message.includes("Требуется вход") || message.includes("401")) onSessionExpired();
        else scheduleConnect();
      }
    };

    const restoreAfterVisibility = () => {
      if (document.visibilityState !== "visible") return;
      const room = roomRef.current;
      if (room) {
        room.send("portal:heartbeat");
        room.send("portal:sync");
      } else {
        scheduleConnect();
      }
    };

    document.addEventListener("visibilitychange", restoreAfterVisibility);
    window.addEventListener("pageshow", restoreAfterVisibility);
    void connect();
    return () => {
      for(const task of pending.current.values()){clearTimeout(task.timer);task.reject(Error("Соединение закрыто"));}pending.current.clear();
      stopped = true;
      document.removeEventListener("visibilitychange", restoreAfterVisibility);
      window.removeEventListener("pageshow", restoreAfterVisibility);
      if (heartbeat !== undefined) window.clearInterval(heartbeat);
      if (retry !== undefined) window.clearTimeout(retry);
      sessionStorage.removeItem(TOKEN_KEY);
      const room = roomRef.current;
      roomRef.current = null;
      if (room) void room.leave().catch(() => undefined);
    };
  }, [client, onSessionExpired, user?.id]);

  const sendMessage = useCallback((text: string) => {
    if (!roomRef.current || connectionState !== "online") {
      setError("Чат сейчас не подключён");
      return;
    }
    roomRef.current.send("chat:send", text);
  }, [connectionState]);

  const deleteMessage = useCallback((messageId: string) => {
    if (!roomRef.current || connectionState !== "online") {
      setError("Чат сейчас не подключён");
      return;
    }
    roomRef.current.send("chat:delete", messageId);
  }, [connectionState]);

  return {
    snapshot,
    connectionState,
    error,
    sendMessage,
    deleteMessage,
    clearError: useCallback(() => setError(""), []),
    request: useCallback((value:Omit<SocialRequest,"id">)=>new Promise<{room?:SocialRoomCard}>((resolve,reject)=>{
      if(!roomRef.current||connectionState!=="online"){reject(Error("Нет связи с порталом"));return;}
      const id=crypto.randomUUID();
      const timer=window.setTimeout(()=>{pending.current.delete(id);reject(Error("Ответ не получен. Обновите состояние и попробуйте снова."));},8000);
      pending.current.set(id,{resolve,reject,timer});roomRef.current.send("social:request",{...value,id});
    }),[connectionState]),
  };
}
